import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Box
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";


const useStyles = makeStyles((theme) => ({
    dialogPaper: {
        borderRadius: "16px",
        padding: theme.spacing(2),
    },
    dialogContent: {
        ...theme.scrollbarStyles,
    },
    markdownContainer: {
        "& h1": {
            fontSize: "1.5rem",
            fontWeight: "bold",
            color: theme.palette.primary.main,
            textAlign: "center",
            marginBottom: theme.spacing(2),
            borderBottom: "1px solid rgba(0,0,0,0.1)",
            paddingBottom: theme.spacing(1)
        },
        "& h2": {
            fontSize: "1.2rem",
            fontWeight: "bold",
            marginTop: theme.spacing(3),
            marginBottom: theme.spacing(1)
        },
        "& h3": {
            fontSize: "1rem",
            fontWeight: "bold",
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(1)
        },
        "& p": {
            fontSize: "0.875rem",
            lineHeight: 1.6,
            marginBottom: theme.spacing(2),
            textAlign: "justify",
            color: theme.palette.text.primary
        },
        "& ul": {
            paddingLeft: theme.spacing(3),
            marginBottom: theme.spacing(2),
            color: theme.palette.text.primary
        },
        "& li": {
            fontSize: "0.875rem",
            marginBottom: theme.spacing(1)
        },
        "& a": {
            color: theme.palette.primary.main,
            textDecoration: "none",
            fontWeight: "bold",
            "&:hover": {
                textDecoration: "underline",
                color: theme.palette.primary.light
            }
        }
    }
}));

const LegalRequirementModal = ({ open, onClose, type }) => {
    const classes = useStyles();
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setLoading(true);
            const lang = i18n.language || "es";
            // Map common language codes just in case (e.g. pt-BR -> pt)
            const cleanLang = lang.split('-')[0]; 
            const url = `/legal/${type}_${cleanLang}.md`;

            fetch(url)
                .then(res => {
                    if (!res.ok) {
                        return fetch(`/legal/${type}_es.md`); // Fallback to Spanish
                    }
                    return res;
                })
                .then(res => res.text())
                .then(text => {
                    setContent(text);
                    setLoading(false);
                })
                .catch(err => {
                    console.error("Error fetching legal text:", err);
                    setContent("Error al cargar el contenido. Por favor, intente de nuevo.");
                    setLoading(false);
                });
        }
    }, [open, type]);

    // Update dynamically when language changes while modal is open
    useEffect(() => {
        const handleLanguageChange = () => {
            if (open) {
                setLoading(true);
                const lang = i18n.language || "es";
                const cleanLang = lang.split('-')[0]; 
                const url = `/legal/${type}_${cleanLang}.md`;

                fetch(url)
                    .then(res => {
                        if (!res.ok) {
                            return fetch(`/legal/${type}_es.md`);
                        }
                        return res;
                    })
                    .then(res => res.text())
                    .then(text => {
                        setContent(text);
                        setLoading(false);
                    })
                    .catch(err => {
                        console.error("Error fetching legal text:", err);
                        setLoading(false);
                    });
            }
        };

        i18n.on('languageChanged', handleLanguageChange);
        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, [open, type]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ className: classes.dialogPaper }}
        >
            <DialogContent className={classes.dialogContent}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box className={classes.markdownContainer} dir={i18n.language === "ar" ? "rtl" : "ltr"}>
                        <div dangerouslySetInnerHTML={{ __html: content }} />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" variant="contained">
                    {i18n.t("common.close") || "Cerrar"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LegalRequirementModal;
